"use client";

import { Fragment } from "react";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";

type CreditedHiive = {
  name: string;
  slug: string;
  logoImage: unknown;
};

function HiiveLogo({
  hiive,
  className,
}: {
  hiive: CreditedHiive;
  className?: string;
}) {
  const src = mediaUrlFromAsset(
    hiive.logoImage as Parameters<typeof mediaUrlFromAsset>[0],
  );
  return (
    <span
      className={cn(
        "relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-surface bg-surface-alt text-[11px] font-semibold text-text-secondary dark:border-black dark:bg-white/10 dark:text-white/70",
        className,
      )}
    >
      {src ? (
        <Image src={src} alt="" fill sizes="28px" className="object-cover" />
      ) : (
        hiive.name.slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

/**
 * "Hosted by A" — and, on a collaboration, "in collaboration with B and C"
 * with every Hiive's logo stacked, Instagram-collab style. Each name opens
 * that Hiive. Only accepted partners ever reach this component.
 */
export function EventHostCredit({
  host,
  collaborators,
}: {
  host: CreditedHiive;
  collaborators: CreditedHiive[];
}) {
  if (collaborators.length === 0)
    return (
      <BackAwareLink
        href={`/hiives/${host.slug}`}
        className="inline-flex w-fit items-center gap-2 rounded-full bg-surface-alt px-3 py-1.5 text-sm text-text-secondary transition-colors hover:text-brand"
      >
        <span className="text-xs font-medium tracking-wide text-text-secondary uppercase">
          Hosted by
        </span>
        <span className="line-clamp-1 font-medium text-text-primary">
          {host.name}
        </span>
      </BackAwareLink>
    );

  const everyone = [host, ...collaborators];
  return (
    <div className="flex w-full items-center gap-2.5 rounded-2xl bg-surface-alt px-3 py-2 dark:bg-white/[0.06]">
      <span className="flex shrink-0 -space-x-2" aria-hidden="true">
        {everyone.slice(0, 4).map((hiive, index) => (
          <HiiveLogo
            key={hiive.slug}
            hiive={hiive}
            className={cn(index === 0 && "z-10")}
          />
        ))}
      </span>
      <p className="min-w-0 text-[13px] leading-snug text-text-secondary dark:text-white/65">
        <span className="text-[11px] tracking-wide uppercase">Hosted by </span>
        <BackAwareLink
          href={`/hiives/${host.slug}`}
          className="font-semibold text-text-primary hover:text-brand dark:text-white"
        >
          {host.name}
        </BackAwareLink>
        <br />
        in collaboration with{" "}
        {collaborators.map((partner, index) => (
          <Fragment key={partner.slug}>
            {index > 0
              ? index === collaborators.length - 1
                ? " and "
                : ", "
              : null}
            <BackAwareLink
              href={`/hiives/${partner.slug}`}
              className="font-semibold text-text-primary hover:text-brand dark:text-white"
            >
              {partner.name}
            </BackAwareLink>
          </Fragment>
        ))}
      </p>
    </div>
  );
}
